import React from 'react';
import { Segment, Header, Table, Grid, Label } from 'semantic-ui-react';

function RecipeView({ recipe }) {
  if (!recipe) return null;

  const { summary = {}, settings = {} } = recipe;
  const { totals = {}, properties = {}, breakdowns = {}, saturations = {} } = summary;

  const createMarkup = (html) => {
    return { __html: html };
  };

  const formatNumber = (num) => {
    return typeof num === 'number' ? num.toFixed(1) : num;
  };

  return (
    <Segment>
      <Header as="h2">{recipe.name}</Header>
      
      {/* Recipe Info */}
      <Grid stackable>
        <Grid.Column width={16}>
          {recipe.description && (
            <Segment>
              <Header as="h4">Description</Header>
              <div dangerouslySetInnerHTML={createMarkup(recipe.description)} />
            </Segment>
          )}
          {recipe.notes && (
            <Segment>
              <Header as="h4">Notes</Header>
              <div dangerouslySetInnerHTML={createMarkup(recipe.notes)} />
            </Segment>
          )}
        </Grid.Column>
      </Grid>

      {/* Recipe Settings */}
      <Header as="h3">Recipe Settings</Header>
      <Table definition>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Soap Type</Table.Cell>
            <Table.Cell>{settings.soapType?.toUpperCase() || 'NaOH'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Units</Table.Cell>
            <Table.Cell>{settings.uom === 'percent' ? 'Percentages' : settings.totalUom}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Total Weight</Table.Cell>
            <Table.Cell>{settings.totalWeight} {settings.totalUom}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Superfat</Table.Cell>
            <Table.Cell>{settings.superFat}%</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Water Ratio</Table.Cell>
            <Table.Cell>{settings.waterRatio}%</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Fragrance</Table.Cell>
            <Table.Cell>
              {settings.fragrancePpo}
              {settings.fragranceType === 'ratio' ? '% of oils' : ' PPO'}
            </Table.Cell>
          </Table.Row>
          {settings.waterDiscount > 0 && (
            <Table.Row>
              <Table.Cell>Water Discount</Table.Cell>
              <Table.Cell>{settings.waterDiscount}%</Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      {/* Recipe Oils */}
      <Header as="h3">Recipe Oils</Header>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Oil</Table.HeaderCell>
            <Table.HeaderCell>%</Table.HeaderCell>
            <Table.HeaderCell>Weight (g)</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {recipe.oils?.map((oil) => (
            <Table.Row key={oil.id}>
              <Table.Cell>{oil.name}</Table.Cell>
              <Table.Cell>{oil.pivot.percentage?.toFixed(1) || '-'}</Table.Cell>
              <Table.Cell>{oil.pivot.weight?.toFixed(1) || '-'}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Grid stackable columns={2}>
        {/* Recipe Totals */}
        <Grid.Column width={8}>
          <Header as="h3">Recipe Totals</Header>
          <Table basic="very" compact>
            <Table.Body>
              <Table.Row>
                <Table.Cell>Total Water Weight</Table.Cell>
                <Table.Cell>{formatNumber(totals.totalWaterWeight)}g</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Total Lye Weight</Table.Cell>
                <Table.Cell>{formatNumber(totals.totalLye)}g</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Total Oil Weight</Table.Cell>
                <Table.Cell>{formatNumber(totals.totalOilWeight)}g</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Fragrance Weight</Table.Cell>
                <Table.Cell>{formatNumber(totals.fragranceWeight)}g</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Total Batch Weight</Table.Cell>
                <Table.Cell>{formatNumber(totals.totalBatchWeight)}g</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Superfat</Table.Cell>
                <Table.Cell>{formatNumber(totals.totalSuperfat)}%</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Water:Lye Ratio</Table.Cell>
                <Table.Cell>{formatNumber(totals.waterLyeRatio)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Lye Concentration</Table.Cell>
                <Table.Cell>{formatNumber(totals.lyeConcentration)}%</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Saturated:Unsaturated</Table.Cell>
                <Table.Cell>{formatNumber(saturations.saturated)} : {formatNumber(saturations.unsaturated)}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Grid.Column>

        {/* Recipe Properties & Fatty Acids */}
        <Grid.Column width={8}>
          <Header as="h3">Recipe Properties</Header>
          <Table basic="very" compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Property</Table.HeaderCell>
                <Table.HeaderCell>Value</Table.HeaderCell>
                <Table.HeaderCell>Recommended</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell>Bubbly</Table.Cell>
                <Table.Cell>{formatNumber(properties.bubbly)}</Table.Cell>
                <Table.Cell>14 - 46</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Cleansing</Table.Cell>
                <Table.Cell>{formatNumber(properties.cleansing)}</Table.Cell>
                <Table.Cell>12 - 22</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Condition</Table.Cell>
                <Table.Cell>{formatNumber(properties.condition)}</Table.Cell>
                <Table.Cell>44 - 69</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Hardness</Table.Cell>
                <Table.Cell>{formatNumber(properties.hardness)}</Table.Cell>
                <Table.Cell>29 - 54</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Longevity</Table.Cell>
                <Table.Cell>{formatNumber(properties.longevity)}</Table.Cell>
                <Table.Cell>25 - 50</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Stable</Table.Cell>
                <Table.Cell>{formatNumber(properties.stable)}</Table.Cell>
                <Table.Cell>16 - 48</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Iodine</Table.Cell>
                <Table.Cell>{formatNumber(properties.iodine)}</Table.Cell>
                <Table.Cell>41 - 70</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>INS</Table.Cell>
                <Table.Cell>{formatNumber(properties.ins)}</Table.Cell>
                <Table.Cell>136 - 165</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Header as="h3">Fatty Acids %</Header>
          <Table basic="very" compact>
            <Table.Body>
              <Table.Row>
                <Table.Cell>Oleic</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.oleic)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Capric</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.capric)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Lauric</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.lauric)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Stearic</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.stearic)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Caprylic</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.caprylic)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Linoleic</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.linoleic)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Myristic</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.myristic)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Palmitic</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.palmitic)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Linolenic</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.linolenic)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Ricinoleic</Table.Cell>
                <Table.Cell>{formatNumber(breakdowns.ricinoleic)}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Grid.Column>
      </Grid>

      {/* Recipe Metadata */}
      <Segment secondary>
        <Label>
          Visibility
          <Label.Detail>{recipe.visibility ? 'Public' : 'Private'}</Label.Detail>
        </Label>
        <Label>
          Created by
          <Label.Detail>{recipe.user?.name}</Label.Detail>
        </Label>
        <Label>
          Created
          <Label.Detail>{new Date(recipe.created_at).toLocaleDateString()}</Label.Detail>
        </Label>
        {recipe.updated_at !== recipe.created_at && (
          <Label>
            Last Updated
            <Label.Detail>{new Date(recipe.updated_at).toLocaleDateString()}</Label.Detail>
          </Label>
        )}
      </Segment>
    </Segment>
  );
}

export default RecipeView; 